<template>
    <div class="certificates-container" ref="certificatesContainer" @scroll="onScroll">
        <div class="header-actions">
            <div class="search-filter-container">
                <div class="search-container">
                    <input type="text"
                        placeholder="Search student by id, name, certificate id or title"
                        v-model="searchQuery"
                        @keyup.enter="reloadCertificates"
                        class="search-input" />
                </div>

                <div class="actions-group">
                    <ItemSelector
                    :items="allCertificates"
                    :initial-items="selectedCertificates"
                    @items-changed="onCertificatesChanged"
                    >
                        <template #trigger-button="{ toggleMenu, selectedItems }">
                            <button @click="toggleMenu" class="certificates-button">
                                <span class="text-left">
                                    <i class="fas fa-award me-2" />
                                    <template v-if="selectedItems.length === 0">  Select Certificates </template>
                                    <template v-else> Certificate: 
                                        <span class="ms-1" style="color: var(--primary-color);"> 
                                            {{ selectedItems.length }} Selected 
                                        </span>
                                    </template>
                                </span>
                                <i v-if="selectedItems.length > 0" 
                                   class="fa fa-times ms-auto cursor-pointer" 
                                   style="font-size: 1.1rem;" 
                                   @click.stop="onCertificatesChanged([])">
                                </i>
                                <i v-else class="fa fa-caret-down ms-auto dropdown-icon"></i>
                            </button>
                        </template>
                    </ItemSelector>

                    <date-range-picker ref="dateRangePicker" :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy' }"
                        :showWeekNumbers="true" :showDropdowns="true" :autoApply="true" v-model="dateRangeFilter"
                        class="date-input" control-container-class="d-flex align-items-center justify-content-between gap-3 w-100" opens="left"
                        :ranges="dateRangePresets" :maxDate="new Date()">
                        <template v-slot:input="picker">
                            <i class="fal fa-calendar-alt"></i>
                            <span class="text-sm" v-if="picker.startDate || picker.endDate">
                                {{ formatDate(picker.startDate) }} -
                                {{ formatDate(picker.endDate) }}
                            </span>
                            <span class="text-sm" v-else>
                                Select Date Range
                            </span>
                            <i @click="resetDateRangeFilter" class="fa fa-times ms-auto cursor-pointer"
                                v-if="picker.startDate || picker.endDate"></i>
                            <i v-else class="fa fa-caret-down ms-auto"></i>
                        </template>
                    </date-range-picker>

                    <button class="filter-btn" @click="reloadCertificates">
                        Search
                        <i class="fas fa-search certificatesearch-icon"></i>
                    </button>

                    <button id="sort-btn" class="btn filter-btn" @click="toggleSort">
                      <i :class="['fas', sortOrder === 'recent' ? 'fa-sort-amount-up' : 'fa-sort-amount-down']"></i>
                    </button>

                    <b-tooltip target="sort-btn" placement="top">
                         {{ sortOrder === 'recent' ? 'Newest First' : 'Oldest First' }}
                    </b-tooltip>

                    <button class="export-btn filter-btn" :disabled="exporting" @click="exportAsExcel">
                        <template v-if="!exporting">
                            <i class="fas fa-file-export"></i>
                            <span> Export </span>
                        </template>
                        <template v-else>
                            <span class="spinner"></span>
                            <span> Exporting... </span>
                        </template>
                    </button>
                </div>
            </div>
        </div>

        <div class="table-container">
            <SkeletonTableLoader v-if="loading && !loadingMore" :rowCount="7" :columnCount="9" />

            <div v-else-if="!loading && userCertificates.length === 0" class="no-data-container">
                <p class="no-data-text">No certificates found. </p>
            </div>

            <div v-else class="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Certificate Name</th>
                            <th>Student ID</th>
                            <th>Student Name</th>
                            <th>Issued</th>
                            <th>Status</th>
                            <th>Revoked At</th>
                            <th>Valid Until</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="certificate in userCertificates" :key="certificate.userCertificate.id">
                            <td>{{ certificate.userCertificate.id || '-' }}</td>
                            <td>{{ certificate.userCertificate.title || 'N/A' }}</td>
                            <td>{{ certificate.user.id }}</td>
                            <td>{{ getFullName(certificate.user) }}</td>
                            <td>{{ formatDateTime(certificate.userCertificate.issueDate) }}</td>
                            <td>
                                <span v-if="certificate.userCertificate.isRevoked" class="badge badge-revoked">
                                    Revoked
                                </span>
                                <span v-else class="badge badge-active"> Active </span>
                            </td>
                            <td>{{ formatDateTime(certificate.userCertificate.revokedAt) }}</td>
                            <td>{{ formatDateTime(certificate.userCertificate.validUntil) }}</td>

                            <td>
                                <div class="actions-dropdown">
                                    <button class="actions-btn" @click="toggleActionMenu(certificate.userCertificate.id)">
                                        ACTIONS
                                        <i class="fas fa-chevron-down" />
                                    </button>
                                    <div v-if="activeActionMenus.includes(certificate.userCertificate.id)"
                                        class="custom-dropdown-menu">
                                        <button class="action-option" @click="downloadCertificate(certificate)">
                                            <i class="fas fa-download" /> Download
                                        </button>
                                        <button v-if="!certificate.userCertificate.isRevoked"
                                            class="action-option text-red-500"
                                            :disabled="revokingIds.includes(certificate.userCertificate.id)"
                                            @click="revokeCertificate(certificate)"
                                            >
                                            <template v-if="revokingIds.includes(certificate.userCertificate.id)">
                                                <span class="spinner" st></span>
                                                <span>Revoking...</span>
                                            </template>
                                            <template v-else>
                                                <i class="fas fa-ban" /> Revoke
                                            </template>
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-if="loadingMore" class="loading-more-indicator">
                    <div class="spinner"></div>
                    <span>Loading more certificates...</span>
                </div>

                <div v-if="!hasMore && userCertificates.length > 0 && !loadingMore" class="all-fetched-indicator">
                    <i class="fas fa-check-circle tick-icon"></i>
                    <span>All certificates have been loaded.</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SkeletonTableLoader from '@/components/Shared/SkeletonTableLoader.vue'
import ItemSelector from '@/components/Shared/ItemSelector.vue'
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import { mapActions, mapState, mapGetters } from "vuex";
import { formatToDDMMMYY_HHmm, toShortDateString } from "../../../helpers/FormatHelpers";

export default {
    data() {
        return {
            searchQuery: "",
            activeActionMenus: [],
            dateRangeFilter: { startDate: null, endDate: null },
            selectedCertificates: [],
            sortOrder: 'recent',
            loading: false,
            loadingMore: false,
            exporting: false,
            revokingIds: []
        };
    },
    components: { SkeletonTableLoader, DateRangePicker, ItemSelector },
    computed: {
        ...mapState("certificate", ["userCertificates"]),
        ...mapGetters("certificate", ["allCertificates", "isReloading", "hasMore"]),

        dateRangePresets() {
            let today = new Date();
            today.setHours(0, 0, 0, 0);

            let yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            let thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
            let thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return {
                Today: [today, today],
                Yesterday: [yesterday, yesterday],
                "This month": [thisMonthStart, thisMonthEnd],
                "Last month": [
                    new Date(today.getFullYear(), today.getMonth() - 1, 1),
                    new Date(today.getFullYear(), today.getMonth(), 0),
                ],
                "Last 3 months": [
                    new Date(today.getFullYear(), today.getMonth() - 2, 1),
                    today,
                ],
                "Last 6 months": [
                    new Date(today.getFullYear(), today.getMonth() - 5, 1),
                    today,
                ],
                "This year": [
                    new Date(today.getFullYear(), 0, 1),
                    new Date(today.getFullYear(), 11, 31),
                ],
                "Last year": [
                    new Date(today.getFullYear() - 1, 0, 1),
                    new Date(today.getFullYear() - 1, 11, 31),
                ],
            };
        },

        inclusiveEndDate(){
            return this.dateRangeFilter?.endDate
            ? new Date(this.dateRangeFilter.endDate.getFullYear(), 
                    this.dateRangeFilter.endDate.getMonth(), 
                    this.dateRangeFilter.endDate.getDate(), 23, 59, 59, 999).toISOString()
            : null;
        }
    },
    methods: {
        ...mapActions("certificate", [
            "fetchUserCertificates",
            "loadMoreUserCertificates",
            "revokeUserCertificate",
            "exportUserCertificates"
        ]),

        async reloadCertificates() {
            if (this.isReloading) return;

            this.loading = true;

            try {
                await this.fetchUserCertificates({
                    searchQuery: this.searchQuery,
                    isNewestFirst: this.sortOrder === 'recent',
                    startDate: this.dateRangeFilter?.startDate?.toISOString(),
                    endDate: this.inclusiveEndDate,
                    certificateIds: this.selectedCertificates?.map(certificate => certificate.id),
                    reload: true
                });

                this.resetScroll();

            } catch (error) {
                this.$toasted.error(error?.message || "Unable to load user certificates.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    singleton: true,
                    duration: 2000
                });
            } finally {
                this.loading = false;
            }
        },

        onCertificatesChanged(items) {
            this.selectedCertificates = items || [];
        },

        async onScroll() {
            const container = this.$refs.certificatesContainer;
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50 && !this.loadingMore) {
                this.loadingMore = true;
                try{
                    await this.loadMoreUserCertificates({
                        searchQuery: this.searchQuery,
                        isNewestFirst: this.sortOrder === 'recent',
                        startDate: this.dateRangeFilter?.startDate?.toISOString(),
                        endDate: this.inclusiveEndDate,
                        certificateIds: this.selectedCertificates?.map(certificate => certificate.id),
                    });
                } catch (error) {
                    this.$toasted.error(error?.message || "Unable to load user certificates.", {
                        icon: "exclamation-triangle",
                        theme: "outline",
                        position: "bottom-center",
                        singleton: true,
                        duration: 2000
                    });
                } finally {
                    this.loadingMore = false;
                }
            }
        },

        toggleActionMenu(id) {
            if (this.activeActionMenus.includes(id)) {
                this.activeActionMenus = this.activeActionMenus.filter(m => m !== id)
            } else {
                this.activeActionMenus.push(id)
            }
        },


        async downloadCertificate(certificate) {
            try{
                const title = certificate.userCertificate?.title || "certificate";
                const url = certificate.userCertificate.certificateURL;
                const response = await fetch(url);

                if (!response.ok) { throw new Error(); }

                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = (title || "certificate") + ".pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(blobUrl);
            }
            catch (error) {
                this.$toasted.error("Unable to download user certificate. Please try again later.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    singleton: true,
                    duration: 2000
                });
            }
        },

        async revokeCertificate(certificate) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to revoke the certificate "${certificate.userCertificate.title}" for ${certificate.user.firstName} ${certificate.user.lastName}?`,
                {
                title: "Confirm Certificate Revocation",
                okTitle: "Yes, Revoke",
                cancelTitle: "Cancel",
                centered: true,
                size: "md",
                okVariant: "danger"
                }
            );

            if (!confirmed) return;

            const certId = certificate.userCertificate.id;
            this.revokingIds.push(certId); 
            
            try {
                await this.revokeUserCertificate(certId);
                this.$toasted.success("Certificate revoked successfully.", {
                    icon: "check-circle",
                    theme: "outline",
                    position: "bottom-center",
                    duration: 2000
                });
            } catch (error) {
                this.$toasted.error(error?.message || "Failed to revoke certificate.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    duration: 2000
                });
            } finally {
                this.revokingIds = this.revokingIds.filter(id => id !== certId) 
                this.toggleActionMenu(certId);
            }
        },

        async exportAsExcel() {
            if (this.exporting) return;
            this.exporting = true;
            try {
                const requestParams = {
                    searchQuery: this.searchQuery,
                    startDate: this.dateRangeFilter?.startDate?.toISOString(),
                    endDate: this.inclusiveEndDate,
                    certificateIds: this.selectedCertificates?.map(certificate => certificate.id),
                };

                await this.exportUserCertificates(requestParams);
            } catch (error) {
                this.$toasted.error(error.message || "Failed to export certificates.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    singleton: true,
                    duration: 3000
                });
            } finally {
                this.exporting = false;
            }
        },

        formatDateTime(date) {
            if (!date) return "N/A";
            return formatToDDMMMYY_HHmm(date);
        },

        formatDate(date) {
            return toShortDateString(date);
        },

        getFullName(user) {
            return user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";
        },

        resetScroll() {
            this.$nextTick(() => {
                this.$refs.certificatesContainer.scrollTop = 0;
            });
        },

        resetDateRangeFilter() {
            this.dateRangeFilter = { startDate: null, endDate: null };
        },

        toggleSort() {
            this.sortOrder = this.sortOrder === "recent" ? "oldest" : "recent";
        },

    },
    watch: {
        sortOrder: function (newSortOrder, oldSortOrder) {
            if (newSortOrder !== oldSortOrder) {
                this.reloadCertificates();
            }
        },
    },
    created() {
        this.reloadCertificates();
    },
};
</script>


<style scoped>
/* ===== Layout ===== */
.certificates-container {
    position: relative;
    height: calc(100svh - var(--header-height) - 65px);
    padding: 1.5rem;
    padding-bottom: 0;
    overflow: auto;
}

.header-actions {
    margin-bottom: 1.5rem;
}

.search-filter-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
}

.search-container {
    flex-grow: 1;
}

.actions-group {
    display: flex;
    align-items: center;
    gap: 1rem;
}

/* ===== Inputs ===== */
.search-input {
    width: 100%;
    height: 35px;
    padding: 0 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    font-size: 14px;
}

.date-input {
    display: flex;
    justify-content: space-between;
    padding: 0 0.75rem;
    height: 35px;
    font-size: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    background-color: #fff;
    min-width: 230px;
    cursor: pointer;
}

.fa-times:hover {
    color: var(--primary-color);
}

/* ===== Buttons ===== */
.certificates-button, .filter-btn, .actions-btn {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0 0.75rem;
    height: 35px;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    background-color: #fff;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
}
.actions-btn {
    height: 27px;
}
.fa-award, .filter-btn i, .export-btn i, .actions-btn i {
    font-size: 80%;
}
.filter-btn, .export-btn, .actions-btn {
    font-size: 1rem;
}
#sort-btn {
    font-size: large;
}
.filter-btn:hover {
    color: var(--primary-color);
}
#sort-btn:focus, #sort-btn:active, #sort-btn:focus:active {
    outline: none !important;
    box-shadow: none !important;
}
.certificates-button {
    min-width: 230px;
}
.certificates-button:hover i:not(.fa-caret-down),
.date-input:hover i:not(.fa-caret-down) {
    color: var(--primary-color);
}
.export-btn[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ===== Table ===== */
.table-container {
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    margin-bottom: 1rem;
}

table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
}

th,
td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
}

th {
    background-color: #f9fafb;
    font-weight: 500;
    color: #374151;
}

/* ===== Badges ===== */
.badge {
    border-radius: 1rem;
    padding: 0.4rem 0.6rem;
    font-size: 0.7rem;
    font-weight: 500;
    color: #fff;
}

.badge-revoked {
    background: red;
}

.badge-active {
    background: green;
}

/* ===== Dropdowns ===== */
.actions-dropdown,
.filter-dropdown {
    position: relative;
}

.custom-dropdown-menu {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 0.25rem;
    background-color: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 999;
    width: 100%;
}

.action-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 1rem;
    text-align: left;
    border: none;
    background: none;
    cursor: pointer;
}

.action-option:hover {
    background-color: #f9fafb;
}

/* ===== Loaders ===== */
.loading-more-indicator {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    font-size: 0.9rem;
    color: #555;
}

.spinner {
    width: 18px;
    height: 18px;
    border: 2px solid #ddd;
    border-top-color: var(--primary-color);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
    flex-shrink: 0;
    aspect-ratio: 1 / 1;
}

.action-option .spinner {
    width: 14px;
    height: 14px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.all-fetched-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin: 1rem auto;
    color: #10b981;
    font-size: 0.95rem;
    font-weight: 500;
}

.tick-icon {
    font-size: 1.1rem;
    color: #10b981;
}

/* ===== No Data ===== */
.no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    margin-top: 10px;
    color: #333;
    text-align: center;
}

.no-data-text {
    font-size: 16px;
    font-weight: 500;
}
</style>

